import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { initAuth } from './lib/auth';
import { RoleGate } from './guards/RoleGate';

/** Catch-all 404: redirects to home and tells crawlers not to index the URL */
function NotFound() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);
  return <Navigate to="/" replace />;
}

// Public routes
import { LoginPage } from './routes/public/LoginPage';
import { RegisterPage } from './routes/public/RegisterPage';
// LandingPage kept for easy revert: import { LandingPage } from './routes/public/LandingPage';
import { ComingSoonPage } from './routes/public/ComingSoonPage';
import { CheckinRedirectPage } from './routes/public/CheckinRedirectPage';
import { ForgotPasswordPage } from './routes/public/ForgotPasswordPage';
import { ResetPasswordPage } from './routes/public/ResetPasswordPage';
import { VerifyEmailPage } from './routes/public/VerifyEmailPage';
import { SkilmalarPage } from './routes/public/SkilmalarPage';
import { PersonuverndPage } from './routes/public/PersonuverndPage';

// User routes
import { UserLayout } from './layouts/UserLayout';
import { DashboardPage } from './routes/user/DashboardPage';
import { PassesPage } from './routes/user/PassesPage';
import { BuyPassPage } from './routes/user/BuyPassPage';
import { HistoryPage } from './routes/user/HistoryPage';

// Staff routes
import { StaffDashboardPage } from './routes/staff/StaffDashboardPage';

// Admin routes
import { AdminDashboardPage } from './routes/admin/AdminDashboardPage';
import { AdminUsersPage } from './routes/admin/AdminUsersPage';
import { AdminPoolsPage } from './routes/admin/AdminPoolsPage';
import { AdminPassesPage } from './routes/admin/AdminPassesPage';
import { AdminAuditPage } from './routes/admin/AdminAuditPage';
import { AdminPricingPage } from './routes/admin/AdminPricingPage';

export function App() {
  useEffect(() => {
    initAuth();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={<ComingSoonPage />} />
        <Route path="/checkin" element={<CheckinRedirectPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route path="/forgot-password" element={<Navigate to="/" replace />} />
        <Route path="/reset-password" element={<Navigate to="/" replace />} />
        <Route path="/verify-email" element={<Navigate to="/" replace />} />
        <Route path="/skilmalar" element={<SkilmalarPage />} />
        <Route path="/personuvernd" element={<PersonuverndPage />} />

        {/* User (authenticated) */}
        <Route element={<RoleGate minRole="user" maxRole="user"><UserLayout /></RoleGate>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/passes" element={<PassesPage />} />
          <Route path="/buy" element={<BuyPassPage />} />
          <Route path="/history" element={<HistoryPage />} />
        </Route>

        {/* Staff */}
        <Route path="/staff" element={<RoleGate minRole="staff"><StaffDashboardPage /></RoleGate>} />

        {/* Admin */}
        <Route path="/admin" element={<RoleGate minRole="admin"><AdminDashboardPage /></RoleGate>} />
        <Route path="/admin/users" element={<RoleGate minRole="admin"><AdminUsersPage /></RoleGate>} />
        <Route path="/admin/pools" element={<RoleGate minRole="admin"><AdminPoolsPage /></RoleGate>} />
        <Route path="/admin/passes" element={<RoleGate minRole="admin"><AdminPassesPage /></RoleGate>} />
        <Route path="/admin/audit" element={<RoleGate minRole="admin"><AdminAuditPage /></RoleGate>} />
        <Route path="/admin/pricing" element={<RoleGate minRole="admin"><AdminPricingPage /></RoleGate>} />

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
